import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LogoLockup, DagazMark } from "@/components/Logo";
import { NAV_LINKS, SITE } from "@/data/content";
import { Menu, X } from "lucide-react";

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <>
            <header
                data-testid="site-nav"
                className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${scrolled ? "bg-chalk/90 backdrop-blur-md border-graphite/10" : "bg-chalk/95 backdrop-blur-sm border-graphite/10"}`}
            >
                <div className="flex items-center justify-between px-6 md:px-12 lg:px-16 h-20">
                    <Link to="/" data-testid="nav-logo" aria-label="Dagaz Development home">
                        <LogoLockup />
                    </Link>
                    <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
                        {NAV_LINKS.map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`font-sans text-[11px] font-medium tracking-[0.22em] uppercase link-rule ${location.pathname.startsWith(l.to) ? "text-graphite" : "text-slate hover:text-graphite"} transition-colors duration-300`}
                            >
                                {l.label}
                            </Link>
                        ))}
                        <Link
                            to="/start-a-project"
                            data-testid="nav-cta-start-project"
                            className="ml-4 inline-flex items-center px-6 py-3 bg-forest text-chalk font-sans text-[11px] font-semibold tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-graphite"
                        >
                            Start a Project
                        </Link>
                    </nav>
                    <button
                        data-testid="nav-menu-button"
                        onClick={() => setOpen(true)}
                        className="lg:hidden p-2 text-graphite"
                        aria-label="Open menu"
                    >
                        <Menu size={22} strokeWidth={1.5} />
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        className="fixed inset-0 z-[60] bg-forest bg-noise flex flex-col"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <div className="flex items-center justify-between px-6 h-20 border-b border-chalk/10">
                            <LogoLockup dark />
                            <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="p-2 text-chalk" aria-label="Close menu">
                                <X size={24} strokeWidth={1.5} />
                            </button>
                        </div>
                        <nav className="flex-1 flex flex-col justify-center px-8 gap-2" aria-label="Mobile">
                            {[{ to: "/", label: "Home" }, ...NAV_LINKS, { to: "/start-a-project", label: "Start a Project" }].map((l, i) => (
                                <span key={l.to} className="block overflow-hidden">
                                    <motion.span
                                        className="block"
                                        initial={{ y: "110%" }}
                                        animate={{ y: "0%" }}
                                        transition={{ duration: 0.7, delay: 0.1 + i * 0.06, ease: [0.65, 0, 0.35, 1] }}
                                    >
                                        <Link
                                            to={l.to}
                                            data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                            className="font-serif font-light text-4xl text-chalk hover:text-gold transition-colors duration-300 py-1 block"
                                        >
                                            {l.label}
                                        </Link>
                                    </motion.span>
                                </span>
                            ))}
                        </nav>
                        <div className="px-8 pb-10 flex items-center justify-between border-t border-chalk/10 pt-6">
                            <DagazMark className="w-6 h-6 text-chalk" />
                            <a href={`mailto:${SITE.email}`} data-testid="mobile-menu-email" className="font-sans text-xs tracking-[0.18em] text-fog">
                                {SITE.email}
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
